import { SrIcon } from "@/components/ui/SrIcon";
import { formatHours } from "@/lib/format";
import { useProjectDetailContext } from "@/contexts/ProjectDetailContext";
import { usePermissions } from "@/hooks/use-permissions";
import { useReassignTimeEntry } from "@/hooks/use-time-entry-overrides";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { BudgetLineRow, TimeEntry } from "@/hooks/use-project-detail";

const NONE_VALUE = "__none__";

interface Props {
  budgetLine: BudgetLineRow;
  projectId: string;
}

/**
 * Liste over time_entries på en budgetlinje hvor brukeren ikke har en
 * tildeling — "løse" timer som trenger et hjem.
 *
 * En time_entry regnes som løs hvis (a) den peker på denne budget_line_id,
 * men (b) brukeren har ingen assignment-rad på samme linje. Det indikerer
 * at noen har timeført mot oppgaven uten at PL har gitt dem en tildeling
 * — typisk etter Monday-import når budgetlinjen mangler tildelt person.
 *
 * NB: 0-timers føringer ignoreres. Monday-import lager spøkelsesføringer
 * (duration 0) når en person var tagget på et Monday-item uten å føre reell
 * tid. De har ingen timer å "gi et hjem", så et varsel om dem er ren støy.
 *
 * Superadmin kan flytte time_entry til en annen budgetlinje fra samme rad
 * (samme dropdown som BudgetLineAdminTools-redigeringsrad).
 */
export function LooseEntriesPanel({ budgetLine: bl, projectId }: Props) {
  const { timeEntries, budgetLines } = useProjectDetailContext();
  const { isSuperadmin } = usePermissions();
  const reassignMut = useReassignTimeEntry();

  const assignedUserIds = new Set(bl.assignments.map((a) => a.user_id));
  const looseEntries = timeEntries.filter(
    (te) =>
      te.budget_line_id === bl.id &&
      te.user_id != null &&
      !assignedUserIds.has(te.user_id) &&
      (te.duration_seconds ?? 0) > 0,
  );

  if (looseEntries.length === 0) return null;

  // Grupper løse timer per bruker for å gi PL en kortfattet visning
  // i stedet for én rad per timeføring.
  const byUser = new Map<string, { name: string; total: number; entries: TimeEntry[] }>();
  for (const te of looseEntries) {
    if (!te.user_id) continue;
    const key = te.user_id;
    const name = te.user?.full_name ?? "?";
    const hours = (te.duration_seconds ?? 0) / 3600;
    if (!byUser.has(key)) byUser.set(key, { name, total: 0, entries: [] });
    const grp = byUser.get(key)!;
    grp.total += hours;
    grp.entries.push(te);
  }

  return (
    <div className="ml-6 mt-2 mb-2 space-y-1">
      {Array.from(byUser.values()).map((grp) => (
        <div
          key={grp.name + grp.entries[0].id}
          className="flex items-center gap-2 px-2 py-1.5 rounded border border-dashed border-destructive/50 bg-destructive/5 text-xs"
        >
          <SrIcon name="alert-triangle" className="w-3 h-3 text-destructive shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-medium">
              <span className="text-primary">{grp.name}</span> har timeført uten tildeling
            </div>
            <div className="text-[10px] text-destructive font-semibold uppercase tracking-wider">
              {grp.entries.length} timeføring{grp.entries.length === 1 ? "" : "er"} · {formatHours(grp.total)}
            </div>
          </div>
          {isSuperadmin && (
            <Select
              value={bl.id}
              onValueChange={(v) => {
                const target = v === NONE_VALUE ? null : v;
                // Flytt alle entries fra denne brukeren på denne linjen samlet.
                for (const te of grp.entries) {
                  reassignMut.mutate({
                    time_entry_id: te.id,
                    project_id: projectId,
                    new_budget_line_id: target,
                  });
                }
              }}
              disabled={reassignMut.isPending}
            >
              <SelectTrigger className="h-7 text-xs w-[200px]">
                <SelectValue placeholder="Flytt til..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_VALUE}>— ufordelt</SelectItem>
                {budgetLines
                  .filter((other) => other.id !== bl.id)
                  .map((other) => (
                    <SelectItem key={other.id} value={other.id}>
                      {other.phase ?? "?"} — {other.description ?? "(uten navn)"}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          )}
        </div>
      ))}
    </div>
  );
}

/** Summer løse timer (timer + count) for en linje — brukes av fordelings-pille. */
export function summarizeLoose(
  budgetLine: BudgetLineRow,
  timeEntries: TimeEntry[],
): { hours: number; count: number } {
  const assignedUserIds = new Set(budgetLine.assignments.map((a) => a.user_id));
  const loose = timeEntries.filter(
    (te) =>
      te.budget_line_id === budgetLine.id &&
      te.user_id != null &&
      !assignedUserIds.has(te.user_id) &&
      (te.duration_seconds ?? 0) > 0,
  );
  const hours = loose.reduce((s, te) => s + (te.duration_seconds ?? 0) / 3600, 0);
  return { hours, count: loose.length };
}
